import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ScopeSidebar } from './ScopeSidebar';

interface SidebarState {
  open: boolean;
  toggle: () => void;
  close: () => void;
}

const Ctx = createContext<SidebarState | null>(null);

export function useSidebar(): SidebarState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSidebar must be used within AppShell');
  return ctx;
}

export function AppShell({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  // Close drawer on ESC
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Close drawer when viewport crosses the mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const handler = (e: MediaQueryListEvent) => { if (e.matches) setOpen(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <Ctx.Provider value={{ open, toggle, close }}>
      <div className={`app-shell ${open ? 'sidebar-open' : ''}`}>
        <ScopeSidebar onNavigate={close} />
        <main className="app-main">{children}</main>
        {open && (
          <div className="sidebar-backdrop" onClick={close} aria-hidden="true" />
        )}
      </div>
    </Ctx.Provider>
  );
}
